import { useReducer, useState } from 'react'

import { Address } from '@models/address'
import { Label } from '@components/UI/Form/Label'
import { Input } from '@components/UI/Form/Input'
import { Button } from '@components/UI/Button'
import { addressReducer } from './reducer'
import { Dialog, DialogProps } from '@components/UI/Dialog'

interface AddressDialogProps extends Omit<DialogProps, 'width'> {
  onConfirm: (address: Address) => void
}

export function AddressDialog(props: AddressDialogProps) {
  const [errors, setErrors] = useState({
    street: false,
    city: false,
    state: false,
    zipCode: false
  })
  const [state, dispatch] = useReducer(addressReducer, {
    street: '',
    city: '',
    state: '',
    zipCode: '',
    isMissing: true
  })

  function confirmAddress() {
    if (state.street && state.city && state.state && state.zipCode) {
      props.onConfirm(state)
      props.handleClose()
    } else {
      setErrors({
        street: !state.street,
        city: !state.city,
        state: !state.state,
        zipCode: !state.zipCode
      })
    }
  }

  return (
    <Dialog isOpen={props.isOpen} handleClose={props.handleClose} width="md">
      <div className="p-8">
        <h1 className="font-black tracking-tight mb-8 text-2xl">
          Add an Address
        </h1>
        <p className="mb-8">
          If we couldn't find your address, you can enter in your details here.
        </p>
        <section className="mb-4">
          <Label htmlFor="address-dialog-street">Street Address</Label>
          <Input
            id="address-dialog-street"
            name="street"
            value={state.street}
            onChange={e =>
              dispatch({ field: 'street', value: e.currentTarget.value })
            }
            required
            placeholder="Street Address"
          />
          {errors.street && (
            <p className="text-red-400 text-sm mt-1">
              Please provide a street address
            </p>
          )}
        </section>
        <section className="mb-4">
          <Label htmlFor="address-dialog-city">City</Label>
          <Input
            id="address-dialog-city"
            name="city"
            value={state.city}
            onChange={e =>
              dispatch({ field: 'city', value: e.currentTarget.value })
            }
            placeholder="City"
            required
          />
          {errors.city && (
            <p className="text-red-400 text-sm mt-1">Please provide a city</p>
          )}
        </section>
        <section className="mb-4">
          <Label htmlFor="address-dialog-state">State</Label>
          <Input
            id="address-dialog-state"
            name="state"
            maxLength={2}
            placeholder="ST"
            value={state.state}
            onChange={e =>
              dispatch({ field: 'state', value: e.currentTarget.value })
            }
            className="uppercase"
            required
          />
          {errors.state && (
            <p className="text-red-400 text-sm mt-1">Please provide a state</p>
          )}
        </section>
        <section className="mb-8">
          <Label htmlFor="address-dialog-zipCode">Zip Code</Label>
          <Input
            id="address-dialog-zipCode"
            name="zipCode"
            placeholder="12345"
            maxLength={5}
            value={state.zipCode}
            onChange={e =>
              dispatch({ field: 'zipCode', value: e.currentTarget.value })
            }
            required
          />
          {errors.zipCode && (
            <p className="text-red-400 text-sm mt-1">
              Please provide a zip code
            </p>
          )}
        </section>
        <section className="flex justify-center">
          <Button type="button" onClick={confirmAddress}>
            Confirm Address
          </Button>
        </section>
      </div>
    </Dialog>
  )
}
